import { AuthenticationUser, useRefreshTokenMutation } from '@codezo/graphql';
import { Center, Loader, Stack, Text } from '@mantine/core';
import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { scheduleTokenRefresh } from '../utils/tokenScheduler';
import { getStoredRefreshToken, storeRefreshToken } from '../utils/tokenStorage';

interface ReAuthenticateCallbackProps {
	setUser: (_user: AuthenticationUser) => void;
	setAccessToken: (_token: string) => void;
}

// Global flag to prevent multiple simultaneous re-auth attempts
let isReAuthenticating = false;

export function ReAuthenticateCallback({ setUser, setAccessToken }: ReAuthenticateCallbackProps) {
	const navigate = useNavigate();
	const hasRun = useRef(false);

	const hash = location.hash.substring(1);
	const prevPath = new URLSearchParams(hash).get('prevPath');

	const [refreshTokenMutation] = useRefreshTokenMutation({
		onError: () => {
			// Error handled by mutation onError
			window.location.replace(import.meta.env.VITE_PUBLIC_APP_URL || '/auth/login');
		},
	});

	useEffect(() => {
		if (hasRun.current || isReAuthenticating) {
			// Removed console.log
			return;
		}

		hasRun.current = true;
		isReAuthenticating = true;

		const attemptReAuthentication = async () => {
			const storedRefreshToken = getStoredRefreshToken();

			if (!storedRefreshToken) {
				window.location.replace(import.meta.env.VITE_PUBLIC_APP_URL || '/auth/login');
				return;
			}

			try {
				const result = await refreshTokenMutation({
					variables: {
						token: storedRefreshToken,
					},
				});

				if (result.data?.refreshToken) {
					const { user, token, refreshToken } = result.data.refreshToken;

					// Map backend user to auth user
					setUser(user as AuthenticationUser);
					setAccessToken(token);

					// Store the new refresh token
					storeRefreshToken(refreshToken);

					// Schedule automatic token refresh
					scheduleTokenRefresh(token);

					navigate(prevPath || '/dashboard', { replace: true });
				} else {
					window.location.replace(import.meta.env.VITE_PUBLIC_APP_URL || '/auth/login');
				}
			} catch {
				// Error already handled by onError
				window.location.replace(import.meta.env.VITE_PUBLIC_APP_URL || '/auth/login');
			}
		};

		attemptReAuthentication();
	}, [refreshTokenMutation, setUser, setAccessToken, navigate, prevPath]);

	return (
		<Center h="100vh">
			<Stack align="center" gap="lg">
				<Loader size="lg" color="codezo" />
				<Stack align="center" gap="xs">
					<Text size="lg" fw={500}>
						Refreshing your session...
					</Text>
					<Text size="sm" c="dimmed">
						Please wait while we restore your access.
					</Text>
				</Stack>
			</Stack>
		</Center>
	);
}
